import {
  Popover,
  PopoverBody,
  PopoverContent,
  PopoverTrigger,
  HStack,
  PlacementWithLogical,
  Box,
  Icon,
  IconProps,
  ThemeTypings,
  As
} from "@chakra-ui/react";
import {
  EmailShareButton,
  FacebookShareButton,
  LinkedinShareButton,
  TwitterShareButton
} from "react-share";
import { ReactNode } from "react";
import { FaXTwitter } from "react-icons/fa6";
import { FiFacebook, FiLinkedin, FiMail } from "react-icons/fi";
import { useTranslator } from "@eo-locale/react";
import dynamic from "next/dynamic";

interface SocialShareProps {
  title: string;
  shareUrl: string;
  trigger: ReactNode;
  placement?: PlacementWithLogical;
}

function SocialShare(props: SocialShareProps) {
  const { translate } = useTranslator();
  const { placement, shareUrl, trigger, title } = props;

  const ShareIcon = (
    props: IconProps & {
      icon: As<any>;
      colorScheme: ThemeTypings["colorSchemes"];
    }
  ) => {
    const { colorScheme, icon, ...rest } = props;

    return (
      <Box
        as="span"
        boxSize={8}
        display="flex"
        borderRadius="full"
        alignItems="center"
        justifyContent="center"
        background={`${colorScheme}.500`}
        _hover={{
          background: `${colorScheme}.600`,
          svg: { background: `${colorScheme}.600` }
        }}
      >
        <Icon as={icon} boxSize={4} color="white" {...rest} />
      </Box>
    );
  };

  return (
    <Popover placement={placement || "auto-end"}>
      <PopoverTrigger>
        <Box display="inline-block">{trigger}</Box>
      </PopoverTrigger>
      <PopoverContent width="full" borderRadius="lg" borderColor="gray.200">
        <PopoverBody
          as={HStack}
          spacing={4}
          py={{ xl: "0.8rem", base: 4 }}
          px={{ xl: 4, base: "0.8rem" }}
          sx={{
            button: {
              height: 8,
              outline: "none",
              borderRadius: "full",
              _focusVisible: { boxShadow: "outline" }
            }
          }}
        >
          <FacebookShareButton title={title} url={shareUrl}>
            <ShareIcon
              icon={FiFacebook}
              colorScheme="facebook"
              aria-label={translate("common.socialMedia.shareToFacebook")}
            />
          </FacebookShareButton>
          <TwitterShareButton title={title} url={shareUrl}>
            <ShareIcon
              icon={FaXTwitter}
              colorScheme="x"
              aria-label={translate("common.socialMedia.shareToX")}
            />
          </TwitterShareButton>
          <LinkedinShareButton title={title} url={shareUrl}>
            <ShareIcon
              icon={FiLinkedin}
              colorScheme="linkedin"
              aria-label={translate("common.socialMedia.shareToLinkedIn")}
            />
          </LinkedinShareButton>
          <EmailShareButton body={title} separator={"\n"} url={shareUrl}>
            <ShareIcon
              icon={FiMail}
              colorScheme="brand"
              aria-label={translate("common.socialMedia.shareToEmail")}
            />
          </EmailShareButton>
        </PopoverBody>
      </PopoverContent>
    </Popover>
  );
}

const SocialShareWithNoSSR = dynamic(() => Promise.resolve(SocialShare), {
  ssr: false
});

export { SocialShareWithNoSSR as SocialShare };
export type { SocialShareProps };
